import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { User, LoginCredentials, TokenResponse, SwitchRoleResponse } from '../../types';
import {
  setTokens,
  clearTokens,
  getDecodedToken,
  getAccessToken,
  isTokenExpired,
} from '../../utils/auth';
import { getCurrentUser } from '../../services/userService';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  switchingRole: boolean;  // Loading state for role switching
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  switchingRole: false,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      const response = await api.post<TokenResponse>('/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, refresh_token } = response.data;
      setTokens(access_token, refresh_token);

      // Get user info from token
      const decoded = getDecodedToken();
      if (decoded) {
        // Create user object from enhanced token data
        const user: User = {
          id: decoded.sub,
          username: decoded.name,
          email: decoded.email,
          email_verified: decoded.email_verified ?? false,
          is_active: decoded.email_verified ?? true,
          is_platform_admin: decoded.is_platform_admin,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          login_mode: decoded.login_mode,  // Track how the user logged in
          available_roles: decoded.available_roles,  // All roles this user can access
        };

        // Optionally still fetch full user details from /me endpoint for additional data
        try {
          const userDetails = await getCurrentUser();
          if (userDetails) {
            // Preserve login_mode and available_roles from token
            return {
              ...userDetails,
              login_mode: decoded.login_mode,
              available_roles: decoded.available_roles,
            };
          }
        } catch (error) {
          console.log(
            'Could not fetch additional user details, using token data'
          );
        }

        return user;
      }

      throw new Error('Failed to decode token');
    } catch (error: any) {
      // Extract the error message from the response
      let message = 'Login failed. Please try again.';
      
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        message = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Handle FastAPI validation errors
        message = errorDetail[0].msg || message;
      } else if (error.message) {
        message = error.message;
      }
      
      return rejectWithValue(message);
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async () => {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('Failed to fetch user details');
    }
    return user;
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({
    userId,
    oldPassword,
    newPassword,
  }: {
    userId: string;
    oldPassword: string;
    newPassword: string;
  }) => {
    await api.post(`/change-password/${userId}`, {
      old_password: oldPassword,
      new_password: newPassword,
    });
  }
);

export const logoutUser = createAsyncThunk('auth/logout', async () => {
  try {
    // Call the logout endpoint to log the activity
    await api.post('/logout');
  } catch (error: any) {
    // A 401 error is expected if the token is already invalid/expired
    // Only log other errors
    if (error?.response?.status !== 401) {
      console.error('Logout endpoint failed:', error);
    }
  }
  // Clear tokens locally regardless of API response
  clearTokens();
  // Clear any registration context
  localStorage.removeItem('pendingClubRegistration');
});

export const switchRole = createAsyncThunk(
  'auth/switchRole',
  async (targetRole: 'admin' | 'club_manager' | 'pilot', { rejectWithValue }) => {
    try {
      const response = await api.post<SwitchRoleResponse>(
        `/me/switch-role?target_role=${targetRole}`
      );

      const { access_token, refresh_token, switched_to, redirect_path, available_roles } = response.data;
      setTokens(access_token, refresh_token);

      // Get updated user info from the new token
      const decoded = getDecodedToken();
      if (decoded) {
        // Fetch full user details
        try {
          const userDetails = await getCurrentUser();
          if (userDetails) {
            return {
              user: {
                ...userDetails,
                login_mode: decoded.login_mode,
                available_roles: decoded.available_roles,
              },
              switched_to,
              redirect_path,
            };
          }
        } catch {
          // Fall back to token data
        }

        return {
          user: {
            id: decoded.sub,
            username: decoded.name,
            email: decoded.email,
            email_verified: decoded.email_verified ?? false,
            is_active: decoded.email_verified ?? true,
            is_platform_admin: decoded.is_platform_admin,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            login_mode: decoded.login_mode,
            available_roles: decoded.available_roles,
          } as User,
          switched_to,
          redirect_path,
        };
      }

      throw new Error('Failed to decode token after role switch');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to switch role';
      return rejectWithValue(message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      clearTokens();
      // Clear any registration context
      localStorage.removeItem('pendingClubRegistration');
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    checkAuth: (state) => {
      const token = getAccessToken();
      if (token && !isTokenExpired(token)) {
        const decoded = getDecodedToken();
        if (decoded) {
          state.isAuthenticated = true;
          // Create user object from enhanced token data
          state.user = {
            id: decoded.sub,
            username: decoded.name,
            email: decoded.email,
            email_verified: decoded.email_verified ?? false,
            is_active: decoded.email_verified ?? true,
            is_platform_admin: decoded.is_platform_admin,
            created_at: '',
            updated_at: '',
            login_mode: decoded.login_mode,
            available_roles: decoded.available_roles,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string || action.error.message || 'Login failed';
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.error = action.error.message || 'Password change failed';
      })
      .addCase(
        fetchCurrentUser.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.user = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch user details';
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      // Switch role handlers
      .addCase(switchRole.pending, (state) => {
        state.switchingRole = true;
        state.error = null;
      })
      .addCase(switchRole.fulfilled, (state, action) => {
        state.switchingRole = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(switchRole.rejected, (state, action) => {
        state.switchingRole = false;
        state.error = action.payload as string || 'Failed to switch role';
      });
  },
});

export const { logout, clearError, setError, checkAuth } = authSlice.actions;
export default authSlice.reducer;
